/**
 * El diálogo de confirmación de lo que no se puede deshacer.
 *
 * Sustituye a los tres `window.confirm` que había —borrar asignatura, borrar cuestionario, borrar
 * pregunta—. El diálogo del navegador tiene tres problemas que importan aquí: hereda el idioma
 * del navegador y no el de la aplicación, así que una profesora con la interfaz en catalán veía
 * «Cancelar / OK» en inglés; no se puede dar formato, así que el nombre de lo que se va a borrar
 * no se distingue del resto; y bloquea el hilo, de modo que ni siquiera se puede consultar antes
 * cuántas cosas se lleva por delante.
 *
 * Usa `<dialog>` nativo con `showModal()`, que ya trae la trampa de foco, el cierre con Escape y
 * el `aria-modal`. Reimplementar eso a mano es una fuente conocida de trampas de foco.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Boton } from './boton.js';

export interface DialogoConfirmacionProps {
  readonly abierto: boolean;
  readonly titulo: string;
  readonly cuerpo: React.ReactNode;
  /** Texto del botón que confirma. Debe decir qué hace, no «Aceptar». */
  readonly confirmar: string;
  readonly destructivo?: boolean;
  readonly onConfirmar: () => void;
  readonly onCancelar: () => void;
}

export function DialogoConfirmacion({
  abierto,
  titulo,
  cuerpo,
  confirmar,
  destructivo = false,
  onConfirmar,
  onCancelar,
}: DialogoConfirmacionProps): React.JSX.Element {
  const { t } = useTranslation();
  const referencia = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogo = referencia.current;
    if (dialogo === null) return;
    if (abierto && !dialogo.open) dialogo.showModal();
    if (!abierto && dialogo.open) dialogo.close();
  }, [abierto]);

  return (
    <dialog
      ref={referencia}
      // Escape y el clic en el fondo cancelan: en un diálogo destructivo, la salida siempre tiene
      // que ser la fácil.
      onCancel={(evento) => {
        evento.preventDefault();
        onCancelar();
      }}
      onClick={(evento) => {
        if (evento.target === referencia.current) onCancelar();
      }}
      className="rounded-card border-line bg-surface text-text m-auto w-[min(28rem,90vw)] border-2 p-0 backdrop:bg-black/70"
    >
      <div className="flex flex-col gap-4 p-6">
        <h2 className="font-display text-h3 font-bold">{titulo}</h2>
        <div className="text-body text-text-dim">{cuerpo}</div>
        <div className="flex flex-wrap justify-end gap-2">
          <Boton variante="secundaria" onClick={onCancelar}>
            {t('common.cancel')}
          </Boton>
          <Boton
            variante={destructivo ? 'destructiva' : 'primaria'}
            onClick={onConfirmar}
            autoFocus
          >
            {confirmar}
          </Boton>
        </div>
      </div>
    </dialog>
  );
}
